"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowUpRight, Check, Calendar, Users, Plane, Split, Layers, ShieldCheck, Sparkles, BedDouble, FileSpreadsheet } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  icon: Users,
  label: "Family alignment survey framework"
}, {
  icon: BedDouble,
  label: "Dynamic multi-villa & stateroom allocator"
}, {
  icon: Split,
  label: "Weighted multi-household expense splitting"
}, {
  icon: Plane,
  label: "Live flight tracker & group logistics matrices"
}];
const mockRows = [{
  room: "Villa Master Suite 1",
  occupants: "Grandparents (Robert & Clara)",
  status: "Confirmed",
  badgeColor: "bg-muted text-foreground"
}, {
  room: "Sea View Guest Annex",
  occupants: "Marcus & Sarah + 2 Kids",
  status: "Confirmed",
  badgeColor: "bg-muted text-foreground"
}, {
  room: "Garden Pavilion Suite",
  occupants: "Elena & Guest",
  status: "Pending Diet",
  badgeColor: "bg-accent text-accent-foreground"
}];
export default function Hero() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="hero" data-nav="light" className="relative bg-background text-foreground pt-24 pb-16 lg:pt-32 lg:pb-24 border-b border-border overflow-hidden">
      {/* Decorative architectural orbit hairline */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-1/4 w-[600px] h-[600px] rounded-full border border-border/40 opacity-70" />

      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          
          {/* Left Column: Product Positioning & Call to Action */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col justify-center text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted rounded-full border border-border w-fit mb-6">
              <Sparkles className="size-3.5 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Notion Operating System
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-serif font-semibold text-foreground tracking-tight leading-[1.12] mb-6">
              The Legacy Expedition Suite
            </h1>

            <p className="text-base sm:text-lg text-foreground/80 font-sans leading-relaxed mb-8">
              The comprehensive Notion system for multi-generational family trip coordinators. Eliminate message-thread chaos, balance competing preferences, and architect effortless milestone gatherings.
            </p>

            {/* Pricing Callout & High-Intent CTA */}
            <div className="p-6 bg-card border border-border rounded-md shadow-xs mb-8">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-3xl sm:text-4xl font-serif font-semibold text-foreground">
                  $69
                </span>
                <span className="text-sm font-medium text-foreground/70">
                  one-time payment / lifetime access
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-sans font-semibold tracking-wide uppercase text-xs h-12 px-7 rounded-sm shadow-sm flex items-center justify-center gap-2">
                  <Link to="https://eleetetvl.gumroad.com/l/legacy-expedition-suite" newTab>
                    <span>Get the Template</span>
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-sans font-medium text-xs tracking-wide uppercase h-12 px-6 rounded-sm">
                  <Link to="/AnchorOrbitMethodPage">
                    Explore Method
                  </Link>
                </Button>
              </div>

              <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-xs text-foreground/70 font-sans">
                <ShieldCheck className="size-4 text-primary" />
                <span>Instant duplicate link to your private Notion workspace.</span>
              </div>
            </div>

            {/* Key Framework Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.label} data-index={i} className="flex items-start gap-2.5">
                    <div className="mt-0.5 p-1 rounded-sm bg-muted text-foreground shrink-0 border border-border">
                      <IconComponent className="size-3.5 text-primary" />
                    </div>
                    <span className="text-xs font-sans text-foreground/80 leading-snug">
                      {item.label}
                    </span>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Right Column: High-Fidelity Notion Dashboard UI Mockup */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7">
            <div className="relative rounded-md border border-border bg-card shadow-xl overflow-hidden">
              
              {/* Notion Window Bar */}
              <div className="bg-muted/70 px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="size-2.5 rounded-full bg-border" />
                  <div className="size-2.5 rounded-full bg-border" />
                  <div className="size-2.5 rounded-full bg-border" />
                  <span className="ml-2 text-xs font-mono font-medium text-foreground/70 tracking-tight">
                    workspace / Amalfi-Legacy-2025
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-sans font-medium px-2 py-0.5 bg-background rounded border border-border text-foreground/80">
                    Anchor & Orbit™ Matrix
                  </span>
                </div>
              </div>

              {/* Workspace Header Strip */}
              <div className="p-5 sm:p-6 bg-card border-b border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded bg-muted border border-border text-primary">
                    <FileSpreadsheet className="size-5" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-serif font-semibold text-foreground leading-none">
                      Milestone Expedition Controller
                    </h2>
                    <p className="text-xs text-foreground/70 mt-1 font-sans">
                      14 Travelers • 3 Households • 10-Day Mediterranean Villa & Yacht Orbit
                    </p>
                  </div>
                </div>

                {/* Notion View Tabs */}
                <div className="flex items-center gap-1 sm:gap-2 mt-4 pt-3 border-t border-border overflow-x-auto text-xs font-sans">
                  <Button type="button" variant="ghost" size="sm" className="h-auto px-3 py-1.5 rounded-sm bg-muted text-foreground font-medium flex items-center gap-1.5 border border-border hover:bg-muted">
                    <BedDouble className="size-3.5 text-primary" />
                    <span>Room Allocator</span>
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-auto px-3 py-1.5 rounded-sm text-foreground/70 hover:bg-muted font-medium flex items-center gap-1.5">
                    <SafeHydrate><Calendar className="size-3.5 text-foreground/60" /></SafeHydrate>
                    <span>Anchor Itinerary</span>
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-auto px-3 py-1.5 rounded-sm text-foreground/70 hover:bg-muted font-medium flex items-center gap-1.5">
                    <Split className="size-3.5 text-foreground/60" />
                    <span>Expense Splits</span>
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-auto px-3 py-1.5 rounded-sm text-foreground/70 hover:bg-muted font-medium flex items-center gap-1.5">
                    <Plane className="size-3.5 text-foreground/60" />
                    <span>Flight Matrix</span>
                  </Button>
                </div>
              </div>

              {/* Interactive Mockup Table / Database View */}
              <div className="p-4 sm:p-6 bg-background/50 overflow-x-auto">
                <div className="min-w-[500px]">
                  
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-3 pb-2 text-[11px] font-mono uppercase tracking-wider text-foreground/60 border-b border-border">
                    <div className="col-span-5 font-semibold">Stateroom / Villa Suite</div>
                    <div className="col-span-4 font-semibold">Assigned Family Branch</div>
                    <div className="col-span-3 text-right font-semibold">Logistics Status</div>
                  </div>

                  {/* Table Rows */}
                  <div className="divide-y divide-border/60">
                    {mockRows.map((row, i) => <div key={row.room} data-index={i} className="grid grid-cols-12 gap-3 py-3 items-center text-xs font-sans">
                        <div className="col-span-5 font-medium text-foreground flex items-center gap-2">
                          <span className="size-1.5 rounded-full bg-primary" />
                          <span>{row.room}</span>
                        </div>
                        <div className="col-span-4 text-foreground/80 font-sans">
                          {row.occupants}
                        </div>
                        <div className="col-span-3 text-right">
                          <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-border ${row.badgeColor}`}>
                            {row.status}
                          </span>
                        </div>
                      </div>)}
                  </div>

                  {/* Sub-Card: Flight Tracker Preview Bar */}
                  <div className="mt-5 p-3.5 bg-card rounded border border-border">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        <Plane className="size-3.5 text-primary" />
                        <span>Rome Fiumicino (FCO) Private Transfer Stream</span>
                      </div>
                      <span className="text-[11px] font-mono text-foreground/70">
                        3 of 3 Inbound Flights Synced
                      </span>
                    </div>
                    <div className="w-full bg-muted h-1.5 rounded-full overflow-hidden">
                      <div className="bg-primary h-full w-full rounded-full" />
                    </div>
                  </div>

                  {/* Anchor & Orbit Philosophy Note in UI */}
                  <div className="mt-4 p-3 bg-muted/50 rounded border border-border flex items-start justify-between gap-3 text-xs text-foreground/80 font-sans">
                    <div>
                      <span className="font-semibold text-foreground">Anchor Principle: </span>
                      Shared evening banquets locked at 19:30. Daytime orbits completely optional per household.
                    </div>
                    <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  </div>

                </div>
              </div>

              {/* Bottom Status Bar */}
              <div className="px-5 py-2.5 bg-muted/40 border-t border-border flex items-center justify-between text-[11px] font-mono text-foreground/70">
                <div className="flex items-center gap-2">
                  <span className="size-2 rounded-full bg-primary inline-block" />
                  <span>Notion 2.0 compatible • iOS / macOS / Windows / Web</span>
                </div>
                <span>E•Leete Travel Design Studio</span>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}